import React, { CSSProperties, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { TrophyFilled } from '@ant-design/icons';
import '../../main.scss';
import { TAreaPosition } from '../GeometryUtils';
import { getAreaInfo, IAreaInfo } from '../../controller/Areas';
import { useAppState } from '../../state/AppState';
import {
  EAreaPrefix,
  IParticipantForUser,
  IPartyForUser,
  IPartyStageForUser,
  TAreaKey,
  TUserId,
} from '../../PartyClientResponse';
import { Popover } from 'antd';
import elToPath from 'element-to-path';
import { interpolate } from 'flubber';
import * as d3 from 'd3';
import { MAX_PEERS } from '../../ApiConstants';
import { useGameState } from '../game/GameState';
import { GAMES_ENABLED } from './AppConstants';

export type TParticipantsByArea = { [area: string]: IParticipantForUser[] };
export type TPositionByArea = { [area: string]: TAreaPosition };
export type TVolume = { volumeTowardsPeer: number; volumeFromPeer: number };
export type TVolumeByArea = {
  [zone in TAreaKey]: TVolume;
};

const getPath = (width: number, height: number) => {
  const square = {
    type: 'element',
    name: 'rect',
    attributes: {
      x: 0,
      y: 0,
      rx: 30,
      ry: 30,
      width,
      height,
    },
  };

  return elToPath(square);
};

const PopoverContent = ({
  info,
  currentParticipants,
  joinArea,
  currentArea,
}: {
  info: IAreaInfo;
  currentParticipants: IParticipantForUser[];
  joinArea: () => void;
  currentArea: TAreaKey;
}) => {
  return (
    <div className='area-popover'>
      <h3 className='area-popover-title'>{info.name}</h3>
      {!!currentParticipants.length && (
        <div className='participant-preview-container'>
          {currentParticipants.map((p) => (
            <div key={p.uid} className='participant-preview'>
              <img
                alt='avatar'
                className='user-avatar'
                width={30}
                height={30}
                style={{}}
                src={p.photoURL}></img>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        {!currentArea.startsWith(EAreaPrefix.LobbyPrefix) &&
        currentParticipants.length >= MAX_PEERS ? (
          <div style={{ color: 'black', display: 'flex' }}>
            <div className='sven' />
            <div
              className='sven-text'
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                fontSize: 18,
              }}>
              <div>sorry, area is full.</div>
              <div>try again later. </div>
            </div>
          </div>
        ) : (
          <div style={{ color: 'gray', fontSize: 18 }}>click to join</div>
        )}
      </div>
    </div>
  );
};

const orignalPathD = new Map<SVGPathElement, string>();

const useWarpingSVGs = ({
  currentAreaSize,
  isCurrentArea,
  icon: ReactIcon,
}: {
  currentAreaSize: { width: number; height: number; offsetTop: number; offsetLeft: number };
  isCurrentArea: boolean;
  icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
      style?: CSSProperties;
    }
  >;
}) => {
  const [ref, setRef] = useState<SVGSVGElement | null>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    (
      ref?.querySelectorAll<HTMLElement>('rect, circle, ellipse, line, polyline, polygon') || []
    ).forEach((el: HTMLElement) => {
      el.replaceWith(elToPath(el));
    });
    setMounted(true);
  }, [ref, setMounted]);

  const prevIsCurrentRef = useRef<boolean>(false);
  const prevAreaSizing = useRef<{ width: number; height: number }>(currentAreaSize);

  useEffect(() => {
    let delay = false;
    if (
      prevAreaSizing.current.height !== currentAreaSize.height ||
      prevAreaSizing.current.width !== currentAreaSize.width
    ) {
      delay = true;
    }
    prevAreaSizing.current.height = currentAreaSize.height;
    prevAreaSizing.current.width = currentAreaSize.width;

    if (!mounted) {
      return;
    }

    // don't initiate a transition unless isCurrentArea changes
    if (prevIsCurrentRef.current === isCurrentArea) {
      return;
    }
    prevIsCurrentRef.current = isCurrentArea;

    const toPath = getPath(currentAreaSize.width, currentAreaSize.height);

    const paths = ref?.querySelectorAll('path') || [];
    let trans: (() => void)[] = [];

    const TRANSITION_DURATION = 750;

    const viewBox = isCurrentArea
      ? `0 0 ${currentAreaSize.width} ${currentAreaSize.height}`
      : `0 0 1000 1000`;

    d3.select(ref).transition().duration(2000).attr('viewBox', viewBox);

    paths.forEach((p: SVGPathElement) => {
      if (!orignalPathD.get(p)) {
        orignalPathD.set(p, p.getAttribute('d') || '');
      }

      let current;
      let next;
      if (isCurrentArea) {
        current = orignalPathD.get(p) || '';
        next = toPath;
      } else {
        current = toPath;
        next = orignalPathD.get(p) || '';
      }

      const interpolator = interpolate(current, next);

      const el = d3.select(p);
      let transition = el.transition();

      if (delay) {
        transition = transition.delay(2000);
      }

      if (isCurrentArea) {
        transition = transition.duration(TRANSITION_DURATION * 2);
      } else {
        transition = transition.duration(TRANSITION_DURATION * 2);
      }

      if (isCurrentArea) {
        transition = transition.style('fill', '#e45683');
      } else {
        transition = transition.attr('style', null);
      }

      transition
        .attrTween('d', function () {
          return interpolator;
        })
        .on('end', () => {
          if (isCurrentArea) {
            el.transition().duration(100).style('fill', 'transparent');
          }
        });

      trans.push(() => {
        el.interrupt();
        el.attr('d', orignalPathD.get(p) || '');
      });
    });
  }, [ref, isCurrentArea, mounted, currentAreaSize.width, currentAreaSize.height]);

  const Icon = useCallback(() => {
    return <ReactIcon ref={setRef} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRef]);

  return {
    Icon,
  };
};

export const PartyAreaBackground = ({
  areaKey,
  onJoinArea,
  currentParticipants,
  party,
  stage,
  style,
  radius,
  currentAreaSize,
}: {
  areaKey: TAreaKey;
  onJoinArea: (area: TAreaKey) => Promise<void>;
  currentParticipants: IParticipantForUser[];
  party: IPartyForUser;
  stage: IPartyStageForUser;
  style: React.CSSProperties;
  radius: number;
  currentAreaSize: { width: number; height: number; offsetTop: number; offsetLeft: number };
}) => {
  const { user, allParticipants, currentArea } = useAppState();
  const features = getAreaInfo(areaKey, party);
  const [isShown, setIsShown] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const isInGame = !!features.currentGameId;

  const onMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsShown(true);
    }, 500);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [setIsShown, timeoutRef]);

  const onMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsShown(false);
  }, [setIsShown, timeoutRef]);
  const isCurrentArea = !!user && stage.participants[user.uid as TUserId].currentArea === areaKey;

  const { Icon } = useWarpingSVGs({ currentAreaSize, isCurrentArea, icon: features.icon });

  const joinArea = useCallback(() => {
    setIsShown(false);
    onJoinArea(areaKey as TAreaKey);
  }, [onJoinArea, areaKey, setIsShown]);

  // Show tot num of participants for the lobby badge (treat everyone as if they are always in the "lobby")
  const numParticipantsForBadge =
    areaKey === EAreaPrefix.LobbyPrefix
      ? allParticipants.filter((p) => p.active).length
      : currentParticipants.length;

  return (
    <Fragment>
      <div
        style={style}
        className={`area-background ${isCurrentArea ? 'current-area' : 'clickable-area'}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={joinArea}>
        <Popover
          visible={!isCurrentArea && isShown}
          content={
            <PopoverContent
              info={features}
              currentParticipants={currentParticipants}
              joinArea={joinArea}
              currentArea={currentArea}
            />
          }
          trigger='hover'
          arrowPointAtCenter
          overlayClassName='area-popover-overlay'
          mouseEnterDelay={0.5}
          placement='top'>
          <div className='area-container'>
            <div className={`area-icon ${isCurrentArea ? '' : 'other'}`}>
              {GAMES_ENABLED && isInGame && !isCurrentArea && (
                <div
                  className='badge glow'
                  style={{
                    fontSize: Math.max((Number(style.height) || 100) / 8, 9),
                    right: 0,
                    marginRight: '1em',
                  }}>
                  <TrophyFilled />
                </div>
              )}
              {!!numParticipantsForBadge && !isCurrentArea && (
                <div
                  className='badge'
                  style={{ fontSize: Math.max((Number(style.height) || 100) / 8, 9) }}>
                  {numParticipantsForBadge}
                </div>
              )}
              <div className={`really-area-icon ${isCurrentArea ? '' : 'other'}`}>
                {!(!features.shouldShowArea && isCurrentArea) && <Icon />}
              </div>
            </div>
          </div>
        </Popover>
      </div>
    </Fragment>
  );
};
